import React, { useEffect, useReducer, useRef, useCallback, useState } from 'react';
import api from '../api';
import Grid from './Grid';
import LetterHand from './LetterHand';
import CompletionPopup from './CompletionPopup';
import { getCellKey, parseKey, extractWords, isGridConnected } from '../gridUtils';
import '../styles/BuildProGame.css';

const TOTAL_TILE_COUNT = 25;

const removeOneLetter = (letters, letter) => {
  const index = letters.indexOf(letter);
  if (index === -1) return letters;
  return letters.filter((_, idx) => idx !== index);
};

const isPlayableLetter = (letter) => typeof letter === 'string' && /^[A-Z]$/.test(letter);

const shouldPreviewMegaMonday = () => {
  if (typeof window === 'undefined') return false;
  const params = new URLSearchParams(window.location.search);
  const value = params.get('mega_monday');
  return ['1', 'true', 'yes'].includes((value || '').toLowerCase());
};

const shouldPreviewPointsMode = () => {
  if (typeof window === 'undefined') return false;
  const params = new URLSearchParams(window.location.search);
  const value = params.get('points_mode');
  return ['1', 'true', 'yes'].includes((value || '').toLowerCase());
};

const pointsForWordLength = (length) => {
  if (length <= 4) return 1;
  if (length === 5) return 2;
  if (length === 6) return 3;
  if (length === 7) return 5;
  return 11;
};

const getFinalTileTarget = (bankLength) => {
  return TOTAL_TILE_COUNT - bankLength;
};

const getPlaceableTileTarget = (state) => {
  return getFinalTileTarget(state.bank.length) - state.queue.length;
};

const getRecoveredHand = (state) => {
  if (!state.sourceLetters || state.sourceLetters.length !== TOTAL_TILE_COUNT) {
    return [...state.hand, ...Object.values(state.grid)].filter(isPlayableLetter);
  }

  let recovered = state.sourceLetters.split('');

  state.bank.forEach((letter) => {
    recovered = removeOneLetter(recovered, letter);
  });

  state.queue.forEach((letter) => {
    recovered = removeOneLetter(recovered, letter);
  });

  return recovered;
};

const getHandForGrid = (state, grid) => {
  if (!state.sourceLetters || state.sourceLetters.length !== TOTAL_TILE_COUNT) {
    return state.hand.filter(isPlayableLetter);
  }

  let hand = state.sourceLetters.split('');

  state.bank.filter(isPlayableLetter).forEach((letter) => {
    hand = removeOneLetter(hand, letter);
  });

  state.queue.filter(isPlayableLetter).forEach((letter) => {
    hand = removeOneLetter(hand, letter);
  });

  Object.values(grid).filter(isPlayableLetter).forEach((letter) => {
    hand = removeOneLetter(hand, letter);
  });

  return hand;
};

const isGridFullyValid = (grid, validationState) => {
  const gridKeys = Object.keys(grid);
  return gridKeys.length > 0 && gridKeys.every((key) => validationState[key]?.status === 'valid');
};

const clearValidationState = {
  validationState: {},
  validationOverlays: [],
  hasInvalidWords: false,
  isBoardConnected: true,
  validationPending: false,
  error: null
};

const initialGameState = {
  grid: {},
  hand: [],
  bank: [],
  queue: [],
  history: [],
  elapsed: 0,
  phase: 'loading', // loading, playing, submitting, complete
  error: null,
  letterSetId: null,
  sourceLetters: null,
  gameMode: 'standard',
  gameStartTime: null,
  completionData: null,
  submitFailed: false,
  validationState: {}, // maps cell key to {valid: bool, word: string}
  validationOverlays: [],
  hasInvalidWords: false,
  isBoardConnected: true,
  validationPending: false,
  points: 0
};

const gameReducer = (state, action) => {
  switch (action.type) {
    case 'INIT_GAME':
      return {
        ...initialGameState,
        hand: action.payload.hand,
        bank: action.payload.bank,
        queue: action.payload.queue,
        letterSetId: action.payload.letterSetId,
        sourceLetters: action.payload.sourceLetters,
        gameMode: action.payload.gameMode || 'standard',
        gameStartTime: Date.now(),
        phase: 'playing'
      };

    case 'PLACE_TILE':
      const { row, col, letter } = action.payload;
      const key = getCellKey(row, col);
      const newGrid = { ...state.grid };

      // Check if cell already occupied (use 'in' to catch undefined values too)
      if (key in newGrid) {
        console.log('PLACE_TILE REJECTED: Cell already occupied', key);
        return state;
      }

      const letterIndex = state.hand.indexOf(letter);
      const newHand = letterIndex === -1
        ? state.hand
        : state.hand.filter((_, idx) => idx !== letterIndex);
      const newHistory = [...state.history, { type: 'place', row, col, letter }];

      newGrid[key] = letter;
      console.log('PLACE_TILE SUCCESS:', { row, col, letter, key }, 'Grid now has', Object.keys(newGrid).length, 'tiles');

      console.log(`PLACE_TILE RESULT: ${Object.keys(newGrid).length} tiles now in grid (${newHand.length} left in hand)`);

      return {
        ...state,
        grid: newGrid,
        hand: newHand,
        history: newHistory,
        submitFailed: false,
        validationPending: true,
        error: null
      };

    case 'REMOVE_TILE':
      const { row: removeRow, col: removeCol } = action.payload;
      const gridAfterRemove = { ...state.grid };
      const letter_removed = gridAfterRemove[getCellKey(removeRow, removeCol)];
      if (!isPlayableLetter(letter_removed)) return state;
      delete gridAfterRemove[getCellKey(removeRow, removeCol)];

      return {
        ...state,
        grid: gridAfterRemove,
        hand: [...state.hand, letter_removed],
        history: [...state.history, { type: 'remove', row: removeRow, col: removeCol, letter: letter_removed }],
        submitFailed: false,
        validationPending: true,
        error: null
      };

    case 'MOVE_TILE':
      const { fromRow, fromCol, toRow, toCol } = action.payload;
      const gridAfterMove = { ...state.grid };
      const movedLetter = gridAfterMove[getCellKey(fromRow, fromCol)];

      if (movedLetter && !gridAfterMove[getCellKey(toRow, toCol)]) {
        delete gridAfterMove[getCellKey(fromRow, fromCol)];
        gridAfterMove[getCellKey(toRow, toCol)] = movedLetter;

        return {
          ...state,
          grid: gridAfterMove,
          history: [...state.history, { type: 'move', fromRow, fromCol, toRow, toCol, letter: movedLetter }],
          submitFailed: false,
          validationPending: true,
          error: null
        };
      }
      return state;

    case 'CLEAR_BOARD':
      const recoveredHand = getRecoveredHand(state);
      console.log('CLEAR_BOARD: Recovered active letters:', recoveredHand.length, recoveredHand);
      return {
        ...state,
        grid: {},
        hand: recoveredHand,
        history: [],
        submitFailed: false,
        validationState: {},
        validationOverlays: [],
        hasInvalidWords: false,
        isBoardConnected: true,
        validationPending: false,
        error: null
      };

    case 'UNDO_LAST':
      if (state.history.length === 0) return state;

      const lastBoardAction = state.history[state.history.length - 1];
      if (typeof lastBoardAction === 'string') {
        return {
          ...state,
          history: state.history.slice(0, -1),
          ...clearValidationState
        };
      }

      const gridAfterUndo = { ...state.grid };
      let handAfterUndo = state.hand.filter(isPlayableLetter);

      if (lastBoardAction.type === 'place') {
        const placedKey = getCellKey(lastBoardAction.row, lastBoardAction.col);
        if (gridAfterUndo[placedKey] === lastBoardAction.letter) {
          delete gridAfterUndo[placedKey];
          handAfterUndo = [...handAfterUndo, lastBoardAction.letter];
        }
      } else if (lastBoardAction.type === 'remove') {
        const removedKey = getCellKey(lastBoardAction.row, lastBoardAction.col);
        if (!(removedKey in gridAfterUndo)) {
          gridAfterUndo[removedKey] = lastBoardAction.letter;
          handAfterUndo = removeOneLetter(handAfterUndo, lastBoardAction.letter);
        }
      } else if (lastBoardAction.type === 'move') {
        const fromKey = getCellKey(lastBoardAction.fromRow, lastBoardAction.fromCol);
        const toKey = getCellKey(lastBoardAction.toRow, lastBoardAction.toCol);
        if (gridAfterUndo[toKey] === lastBoardAction.letter && !(fromKey in gridAfterUndo)) {
          delete gridAfterUndo[toKey];
          gridAfterUndo[fromKey] = lastBoardAction.letter;
        }
      } else {
        return {
          ...state,
          history: state.history.slice(0, -1),
          ...clearValidationState
        };
      }

      return {
        ...state,
        grid: gridAfterUndo,
        hand: handAfterUndo,
        history: state.history.slice(0, -1),
        submitFailed: false,
        ...clearValidationState
      };

    case 'DROP_LETTERS':
      if (state.queue.length >= 2) {
        const newLetters = state.queue.slice(0, 2);
        const newQueue = state.queue.slice(2);
        return {
          ...state,
          hand: [...state.hand, ...newLetters],
          queue: newQueue
        };
      }
      return state;

    case 'EXCHANGE':
      if (state.gameMode === 'points') return state;
      const { givenLetter, receivedLetters } = action.payload;
      const givenLetterIndex = state.hand.indexOf(givenLetter);
      if (givenLetterIndex === -1) return state;

      const handWithoutGiven = state.hand.filter((_, idx) => idx !== givenLetterIndex);
      const newBankWithGiven = [...state.bank, givenLetter];

      if (receivedLetters.length >= 2) {
        const preferredIndices = newBankWithGiven
          .map((letter, idx) => (letter !== givenLetter ? idx : null))
          .filter((idx) => idx !== null);
        const sourcePool = preferredIndices.length >= 2 ? preferredIndices : newBankWithGiven.map((_, idx) => idx);
        const randomIndices = [];
        while (randomIndices.length < 2) {
          const idx = sourcePool[Math.floor(Math.random() * sourcePool.length)];
          if (!randomIndices.includes(idx)) randomIndices.push(idx);
        }

        const exchangedLetters = randomIndices
          .sort((a, b) => b - a)
          .map((idx) => newBankWithGiven[idx]);

        const updatedBank = newBankWithGiven.filter((_, idx) => !randomIndices.includes(idx));

        return {
          ...state,
          hand: [...handWithoutGiven, ...exchangedLetters],
          bank: updatedBank
        };
      }
      return state;

    case 'SET_ELAPSED':
      return {
        ...state,
        elapsed: action.payload
      };

    case 'SET_PHASE':
      return {
        ...state,
        phase: action.payload
      };

    case 'SET_ERROR':
      return {
        ...state,
        error: action.payload
      };

    case 'SET_SUBMIT_FAILED':
      return {
        ...state,
        submitFailed: true
      };

    case 'SET_COMPLETION':
      return {
        ...state,
        phase: 'complete',
        completionData: action.payload
      };

    case 'RESTORE_FROM_SESSION':
      const restoredState = {
        ...initialGameState,
        ...action.payload
      };
      return {
        ...restoredState,
        hand: getHandForGrid(restoredState, restoredState.grid || {}),
        points: restoredState.points || 0,
        validationPending: Object.keys(restoredState.grid || {}).length > 0,
        history: Array.isArray(restoredState.history)
          ? restoredState.history.filter((entry) => typeof entry === 'object' && entry !== null)
          : []
      };

    case 'SET_SOURCE_LETTERS':
      return {
        ...state,
        sourceLetters: action.payload
      };

    case 'UPDATE_VALIDATION':
      return {
        ...state,
        validationState: action.payload.validationState,
        validationOverlays: action.payload.validationOverlays,
        hasInvalidWords: action.payload.hasInvalidWords,
        isBoardConnected: action.payload.isBoardConnected,
        validationPending: action.payload.validationPending,
        points: action.payload.points || 0
      };

    default:
      return state;
  }
};

function BuildProGame({ playerName, onLeaderboard, onPlayAgain, onAlreadyPlayed }) {
  const [gameState, dispatch] = useReducer(gameReducer, initialGameState);
  const [selectedLetter, setSelectedLetter] = useState(null);
  const timerIntervalRef = useRef(null);
  const countdownIntervalRef = useRef(null);
  const countdownRef = useRef(30);
  const validationRunRef = useRef(0);

  // Initialize game
  useEffect(() => {
    const initGame = async () => {
      try {
        console.log('Initializing game for player:', playerName);

        // Always ask the API for today's fixed sequence first. All players derive
        // hand, queue, and bank from this same string.
        console.log('Fetching today\'s game sequence');
        const data = await api.startGame(playerName, {
          megaMonday: shouldPreviewMegaMonday(),
          pointsMode: shouldPreviewPointsMode()
        });
        console.log('Game data received:', data);

        const letters = data.letters;
        if (!letters || letters.length !== TOTAL_TILE_COUNT) {
          throw new Error(`Invalid letters received: expected ${TOTAL_TILE_COUNT}, got ${letters?.length || 0}`);
        }

        const isMegaMonday = data.game_mode === 'mega_monday' || data.is_mega_monday === true;
        const isPointsMode = data.game_mode === 'points' || data.is_points_mode === true;
        const gameMode = isPointsMode ? 'points' : isMegaMonday ? 'mega_monday' : 'standard';

        // Check session storage for saved game
        const savedGame = sessionStorage.getItem(`buildpro_game_${playerName}`);
        if (savedGame) {
          console.log('Restoring game from session storage');
          const parsed = JSON.parse(savedGame);

          const rulesMatch = (parsed.gameMode || 'standard') === gameMode
            && (!isPointsMode || (parsed.bank?.length === 1 && parsed.queue?.length === 0));
          if (rulesMatch && (!parsed.sourceLetters || parsed.sourceLetters === letters)) {
            dispatch({
              type: 'RESTORE_FROM_SESSION',
              payload: {
                ...parsed,
                letterSetId: data.letter_set_id,
                sourceLetters: letters
              }
            });
            return;
          }

          console.log('Ignoring saved game because it does not match today\'s sequence');
          sessionStorage.removeItem(`buildpro_game_${playerName}`);
        }

        // Start new game
        const hand = letters.slice(0, isPointsMode ? 24 : isMegaMonday ? 20 : 6).split('');
        // Keep the unused 25th tile aside so recovery and completion count only 24.
        const bank = letters.slice(isPointsMode ? 24 : 20, 25).split('');
        const queue = isMegaMonday || isPointsMode ? [] : letters.slice(6, 20).split('');

        console.log('Initializing game state:', { hand, bank, queue, gameMode: data.game_mode });

        dispatch({
          type: 'INIT_GAME',
          payload: {
            hand,
            bank,
            queue,
            letterSetId: data.letter_set_id,
            sourceLetters: letters,
            gameMode
          }
        });
      } catch (err) {
        console.error('Game initialization error:', err.message, err);
        if (err.status === 409 || /already submitted today/i.test(err.message || '') || /already played today/i.test(err.message || '')) {
          if (sessionStorage.getItem(`buildpro_game_${playerName}`)) {
            sessionStorage.removeItem(`buildpro_game_${playerName}`);
          }
          if (typeof onAlreadyPlayed === 'function') {
            onAlreadyPlayed();
            return;
          }
        }
        dispatch({ type: 'SET_ERROR', payload: err.message });
      }
    };

    initGame();
  }, [playerName, onAlreadyPlayed]);

  // Timer
  useEffect(() => {
    if (gameState.phase !== 'playing') return;

    timerIntervalRef.current = setInterval(() => {
      dispatch({ type: 'SET_ELAPSED', payload: gameState.elapsed + 1 });
    }, 1000);

    return () => clearInterval(timerIntervalRef.current);
  }, [gameState.phase, gameState.elapsed]);

  // Countdown (30 seconds)
  useEffect(() => {
    if (gameState.phase !== 'playing' || gameState.queue.length === 0) return;

    // Drop immediately only after the current board has finished validating and
    // every occupied cell is green. The pending check prevents this effect from
    // using validation results belonging to the board before the last placement.
    if (
      gameState.hand.length === 0 &&
      !gameState.validationPending &&
      !gameState.hasInvalidWords &&
      gameState.isBoardConnected &&
      isGridFullyValid(gameState.grid, gameState.validationState) &&
      gameState.queue.length > 0
    ) {
      console.log('Dropping letters - hand cleared with valid connected words');
      dispatch({ type: 'DROP_LETTERS' });
      countdownRef.current = 30;
      return;
    }

    countdownIntervalRef.current = setInterval(() => {
      countdownRef.current -= 1;

      if (countdownRef.current <= 0) {
        if (gameState.queue.length > 0) {
          console.log('Dropping letters - 30 second timer expired');
          dispatch({ type: 'DROP_LETTERS' });
        }
        countdownRef.current = 30;
      }
    }, 1000);

    return () => clearInterval(countdownIntervalRef.current);
  }, [
    gameState.phase,
    gameState.queue.length,
    gameState.hand.length,
    gameState.hasInvalidWords,
    gameState.isBoardConnected,
    gameState.validationPending,
    gameState.validationState,
    gameState.grid
  ]);

  // Save game state to session storage
  useEffect(() => {
    if (gameState.phase === 'playing') {
      sessionStorage.setItem(`buildpro_game_${playerName}`, JSON.stringify(gameState));
    }
  }, [gameState, playerName]);

  const validateAndSubmit = useCallback(async () => {
    try {
      dispatch({ type: 'SET_PHASE', payload: 'submitting' });

      const gridArray = Object.entries(gameState.grid).map(([key, letter]) => {
        const { row, col } = parseKey(key);
        return { row, col, letter };
      });

      const validation = await api.validateGrid(gridArray);

      if (!validation.valid) {
        dispatch({ type: 'SET_PHASE', payload: 'playing' });
        dispatch({ type: 'SET_ERROR', payload: validation.error });
        return;
      }

      const result = await api.submitScore(playerName, gridArray, {
        pointsMode: gameState.gameMode === 'points' && shouldPreviewPointsMode()
      });
      sessionStorage.removeItem(`buildpro_game_${playerName}`);

      dispatch({
        type: 'SET_COMPLETION',
        payload: {
          time: result.time_taken,
          points: result.points ?? gameState.points,
          rank: result.rank
        }
      });
    } catch (err) {
      dispatch({ type: 'SET_ERROR', payload: err.message });
      dispatch({ type: 'SET_SUBMIT_FAILED' });
      dispatch({ type: 'SET_PHASE', payload: 'playing' });
    }
  }, [gameState.gameMode, gameState.grid, gameState.points, playerName]);

  // Timed games submit immediately on completion. Points games wait for the
  // player to finalize the board with the submit button.
  useEffect(() => {
    if (
      gameState.gameMode === 'points' ||
      gameState.phase !== 'playing' ||
      gameState.queue.length !== 0 ||
      gameState.hand.length !== 0 ||
      gameState.hasInvalidWords ||
      !gameState.isBoardConnected ||
      gameState.validationPending ||
      !isGridFullyValid(gameState.grid, gameState.validationState) ||
      gameState.submitFailed
    ) {
      return;
    }

    console.log('Auto-submitting timed game - all tiles placed, connected, and valid');
    validateAndSubmit();
  }, [gameState.phase, gameState.grid, gameState.hasInvalidWords, gameState.queue.length, gameState.hand.length, gameState.isBoardConnected, gameState.validationPending, gameState.validationState, gameState.gameMode, gameState.submitFailed, validateAndSubmit]);

  // Validate words in real-time
  useEffect(() => {
    const validateWords = async () => {
      const runId = ++validationRunRef.current;
      const words = extractWords(gameState.grid, 2);
      const connected = isGridConnected(gameState.grid);
      const gridCells = Object.entries(gameState.grid).map(([key, letter]) => {
        const { row, col } = parseKey(key);
        return { key, row, col, letter };
      });

      dispatch({
        type: 'UPDATE_VALIDATION',
        payload: {
          validationState: {},
          validationOverlays: [],
          hasInvalidWords: false,
          isBoardConnected: connected,
          validationPending: true,
          points: 0
        }
      });

      if (words.length === 0) {
        const overlays = gridCells.map((cell) => ({
          key: cell.key,
          word: cell.letter,
          status: 'invalid',
          direction: 'single',
          cells: [{ row: cell.row, col: cell.col }]
        }));

        const validationState = {};
        gridCells.forEach((cell) => {
          validationState[cell.key] = {
            status: 'invalid',
            valid: false,
            word: cell.letter
          };
        });

        dispatch({
          type: 'UPDATE_VALIDATION',
          payload: {
            validationState,
            validationOverlays: overlays,
            hasInvalidWords: gridCells.length > 0,
            isBoardConnected: connected
              ? true
              : false,
            validationPending: false,
            points: 0
          }
        });
        return;
      }

      const evaluations = await Promise.all(
        words.map(async (wordObj) => {
          try {
            const result = await api.checkWord(wordObj.word);
            return { wordObj, valid: result.valid };
          } catch (err) {
            console.error('VALIDATION: Error validating word:', wordObj.word, err);
            return { wordObj, valid: false };
          }
        })
      );

      if (runId !== validationRunRef.current) return;

      const coveredCells = new Set();
      const overlays = [];
      const cellStates = {};
      let hasInvalid = false;
      const points = evaluations.reduce((total, { wordObj, valid }) => (
        total + (valid && connected ? pointsForWordLength(wordObj.word.length) : 0)
      ), 0);

      evaluations.forEach(({ wordObj, valid }) => {
        const status = valid ? (connected ? 'valid' : 'disconnected') : 'invalid';
        if (!valid) hasInvalid = true;

        overlays.push({
          key: wordObj.cells.map((cell) => `${cell.row},${cell.col}`).join('|'),
          word: wordObj.word,
          status,
          direction: wordObj.direction,
          cells: wordObj.cells
        });

        wordObj.cells.forEach((cell) => {
          coveredCells.add(`${cell.row},${cell.col}`);
          const key = getCellKey(cell.row, cell.col);
          const existing = cellStates[key];

          const priority = { invalid: 3, disconnected: 2, valid: 1 };
          if (!existing || priority[status] >= priority[existing.status]) {
            cellStates[key] = {
              status,
              valid,
              word: wordObj.word
            };
          }
        });
      });

      gridCells.forEach((cell) => {
        if (!coveredCells.has(cell.key)) {
          overlays.push({
            key: cell.key,
            word: cell.letter,
            status: 'invalid',
            direction: 'single',
            cells: [{ row: cell.row, col: cell.col }]
          });

          cellStates[cell.key] = {
            status: 'invalid',
            valid: false,
            word: cell.letter
          };
          hasInvalid = true;
        }
      });

      dispatch({
        type: 'UPDATE_VALIDATION',
        payload: {
          validationState: cellStates,
          validationOverlays: overlays,
          hasInvalidWords: hasInvalid,
          isBoardConnected: connected,
          validationPending: false,
          points
        }
      });
    };

    if (Object.keys(gameState.grid).length > 0) {
      validateWords();
    } else {
      validationRunRef.current += 1;
      dispatch({
        type: 'UPDATE_VALIDATION',
        payload: {
          validationState: {},
          validationOverlays: [],
          hasInvalidWords: false,
          isBoardConnected: true,
          validationPending: false,
          points: 0
        }
      });
    }
  }, [gameState.grid]);

  const handlePlaceTile = useCallback((row, col, letter) => {
    if (!isPlayableLetter(letter)) {
      dispatch({ type: 'SET_ERROR', payload: 'Invalid tile' });
      return;
    }

    const key = getCellKey(row, col);

    // Reject placement if cell is already occupied (use 'in' to catch undefined values too)
    if (key in gameState.grid) {
      console.log('❌ REJECTED: Cell occupied at', key, '- current grid size:', Object.keys(gameState.grid).length);
      dispatch({ type: 'SET_ERROR', payload: 'Cell already occupied' });
      return;
    }

    console.log('✓ ACCEPTED: Placing', letter, 'at', key, '- grid will have', Object.keys(gameState.grid).length + 1, 'tiles');
    dispatch({ type: 'PLACE_TILE', payload: { row, col, letter } });
    dispatch({ type: 'SET_ERROR', payload: null });
    setSelectedLetter(null);
  }, [gameState.grid]);

  const handleRemoveTile = useCallback((row, col) => {
    dispatch({ type: 'REMOVE_TILE', payload: { row, col } });
    setSelectedLetter(null);
  }, []);

  const handleMoveTile = useCallback((fromRow, fromCol, toRow, toCol) => {
    dispatch({ type: 'MOVE_TILE', payload: { fromRow, fromCol, toRow, toCol } });
    dispatch({ type: 'SET_ERROR', payload: null });
    setSelectedLetter(null);
  }, []);

  const handleClearBoard = useCallback(() => {
    dispatch({ type: 'CLEAR_BOARD' });
    setSelectedLetter(null);
  }, []);

  const handleUndoLast = useCallback(() => {
    dispatch({ type: 'UNDO_LAST' });
    setSelectedLetter(null);
  }, []);

  const handleExchange = useCallback((letter) => {
    if (gameState.gameMode === 'points') return;
    if (gameState.bank.length < 2) {
      dispatch({ type: 'SET_ERROR', payload: 'Not enough letters in exchange bank' });
      return;
    }

    dispatch({
      type: 'EXCHANGE',
      payload: { givenLetter: letter, receivedLetters: [null, null] }
    });
    setSelectedLetter(null);
  }, [gameState.bank.length, gameState.gameMode]);

  const handleSelectLetter = useCallback((letter) => {
    if (!isPlayableLetter(letter)) return;
    setSelectedLetter((current) => (current === letter ? null : letter));
  }, []);

  const canSubmitFinalBoard = gameState.gameMode === 'points'
    && gameState.phase === 'playing'
    && gameState.queue.length === 0
    && gameState.hand.length === 0
    && Object.keys(gameState.grid).length === 24
    && !gameState.hasInvalidWords
    && gameState.isBoardConnected
    && !gameState.validationPending
    && isGridFullyValid(gameState.grid, gameState.validationState);

  if (gameState.phase === 'loading') {
    if (gameState.error) {
      return (
        <div className="game-loading error">
          <p>Failed to load game</p>
          <p className="error-detail">{gameState.error}</p>
          <button onClick={() => window.location.reload()} className="btn btn-primary">
            Retry
          </button>
        </div>
      );
    }
    return <div className="game-loading">Loading game...</div>;
  }

  if (gameState.phase === 'complete') {
    return (
      <CompletionPopup
        playerName={playerName}
        time={gameState.completionData.time}
        points={gameState.completionData.points}
        gameMode={gameState.gameMode}
        rank={gameState.completionData.rank}
        onLeaderboard={onLeaderboard}
        onPlayAgain={onPlayAgain}
      />
    );
  }

  return (
    <div className={`build-pro-game ${gameState.phase === 'submitting' ? 'is-submitting' : ''}`}>
      <div className="game-container">
        <div className="game-header">
          <div className="game-stat game-stat-primary">Time: {gameState.elapsed}s</div>
          {gameState.gameMode === 'points' && <div className="game-stat game-stat-points">Points: {gameState.points}</div>}
          <div className="game-stat">Placed: {Object.keys(gameState.grid).length}/{getFinalTileTarget(gameState.bank.length)}</div>
          {gameState.queue.length > 0 && (
            <div className="game-stat">Next: {countdownRef.current}s</div>
          )}
          <div className="status-actions">
            <button onClick={handleUndoLast} className="icon-control-btn" aria-label="Undo last move" title="Undo Last" disabled={gameState.phase === 'submitting'}>
              ↶
            </button>
            <button onClick={handleClearBoard} className="icon-control-btn" aria-label="Clear board" title="Clear Board" disabled={gameState.phase === 'submitting'}>
              ×
            </button>
          </div>
        </div>

        {gameState.gameMode === 'points' && (
          <div className="points-reference" aria-label="Word length scoring reference">
            <span>Letters → points:</span>
            <span>2–4 → <strong>1</strong></span>
            <span>5 → <strong>2</strong></span>
            <span>6 → <strong>3</strong></span>
            <span>7 → <strong>5</strong></span>
            <span>8+ → <strong>11</strong></span>
          </div>
        )}

        <Grid
          grid={gameState.grid}
          onPlaceTile={handlePlaceTile}
          onRemoveTile={handleRemoveTile}
          onMoveTile={handleMoveTile}
          hand={gameState.hand}
          selectedLetter={selectedLetter}
          validationState={gameState.validationState}
          validationOverlays={gameState.validationOverlays}
        />

        {gameState.gameMode === 'points' && (
          <div className="final-submit-area">
            <button
              type="button"
              className="btn btn-primary final-submit-btn"
              onClick={validateAndSubmit}
              disabled={!canSubmitFinalBoard}
            >
              {gameState.phase === 'submitting' ? 'Submitting…' : 'Submit Final Board'}
            </button>
            {!canSubmitFinalBoard && gameState.phase !== 'submitting' && (
              <small>Place all 24 tiles in valid, connected words to submit.</small>
            )}
          </div>
        )}

        {gameState.error && <div className="error-message">{gameState.error}</div>}
      </div>

      <LetterHand
        hand={gameState.hand}
        bank={gameState.bank}
        exchangesAllowed={gameState.gameMode !== 'points'}
        onExchange={handleExchange}
        onPlaceTile={handlePlaceTile}
        onReturnTile={handleRemoveTile}
        selectedLetter={selectedLetter}
        onSelectLetter={handleSelectLetter}
      />
    </div>
  );
}

export default BuildProGame;
